import type { UseFormRegisterReturn } from "react-hook-form";
import { cva } from "class-variance-authority";
import { Label } from "./Label";

const inputStyles = cva(
  [
    "w-full min-h-8 rounded-md font-inter text-xs lg:text-sm leading-4 lg:leading-5",
    "bg-gray-50 border px-2 py-1",
    "focus:border-transparent focus:outline-none focus:ring-1 focus:ring-blue-600",
    "transition-all duration-200"
  ],
  {
    variants: {
      hasValue: {
        true: [
          "border-blue-600 text-gray-900 bg-blue-50",
          "focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50"
        ],
        false: ["border-gray-400 text-gray-900"]
      }
    },
    defaultVariants: {
      hasValue: false
    }
  }
);

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  id?: string;
  label?: string;
  type?: string;
  placeholder?: string;
  register?: UseFormRegisterReturn;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  markup?: React.ReactNode;
  "data-testid"?: string;
};

export function Input({
  id,
  label,
  type = "text",
  placeholder,
  register,
  value,
  onChange,
  checked,
  name,
  className = "",
  labelClassName,
  inputClassName,
  markup,
  "data-testid": dataTestId = "input",
  ...rest
}: InputProps) {
  const finalInputClass = inputClassName || inputStyles({ hasValue: false });

  // Support both controlled and react-hook-form modes
  const inputProps = register
    ? { ...rest, ...register }
    : { ...rest, value, onChange, checked, name };

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id} className={labelClassName}>
          {label}
        </Label>
      )}
      <input
        id={id}
        type={type}
        {...inputProps}
        className={finalInputClass}
        placeholder={placeholder}
        data-testid={dataTestId}
      />
      {markup}
    </div>
  );
}
